/**
 * A few convenient utility queries.
 */
import { AsyncDuckDB } from '@duckdb/duckdb-wasm';

import { runQuery } from './runQuery';

export enum TableType {
  Table = 'Table',
  View = 'View',
}

/**
 * Return the table type (table or view) for a given name,
 * or undefined if no such table or view exists.
 */
export const tableType = async (
  db: AsyncDuckDB,
  name: string
): Promise<TableType | undefined> => {
  const arrow = await runQuery(
    db,
    `select table_type from information_schema.tables where table_name = '${name}'`
  );

  if (arrow.numRows === 1) {
    const row = arrow.get(0);
    const type = row?.toArray()[0];
    switch (type) {
      case 'BASE TABLE':
        return TableType.Table;
      case 'VIEW':
        return TableType.View;
      default:
        throw new Error(`Unexpected table type: ${type}`);
    }
  } else {
    return undefined;
  }
};

/**
 * Drop a table or view with a given name.
 */
export const drop = async (db: AsyncDuckDB, name: string) => {
  const type = await tableType(db, name);
  if (type === TableType.Table) {
    await runQuery(db, `drop table if exists "${name}"`);
  } else if (type === TableType.View) {
    await runQuery(db, `drop view if exists "${name}"`);
  }
};

/**
 * Return a mapping from column names to data types, or the empty Map if no such table columns exist.
 */
export const columnTypes = async (
  db: AsyncDuckDB,
  name: string
): Promise<Map<string, string>> => {
  const arrow = await runQuery(
    db,
    `select column_name, data_type from information_schema.columns where table_name = '${name}'`
  );

  const columns = new Map<string, string>();
  for (let i = 0; i < arrow.numRows; i++) {
    const row = arrow.get(i);
    if (row) {
      const [column_name, data_type] = row.toArray();
      columns.set(column_name, data_type);
    }
  }

  return columns;
};
